import { apiFiles } from "@/api/file.api";
import { FileItem } from "@/components/files/FileItem.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { EmployeeDocumentPreviewModal } from "@/pages/employees/employee/widgets/EmployeeDocumentPreviewModal.tsx";
import {
  useEmployeeDocumentSave,
  useOrganizationDocumentSave,
} from "@/queries/use-documents.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiDocument, ApiDocumentSavePayload } from "@/types/document.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import {
  Button,
  FileDropzone,
  FileDropzoneOnDrop,
  Input,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export interface DocumentEditModalProps extends ModalProps {
  folderId: number;
  employeeId: number | null;
  document: ApiDocument | null;
}

export const DocumentEditModal = ({
  employeeId,
  document,
  folderId,
  ...props
}: DocumentEditModalProps) => {
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  const { showError } = useToasts();
  const isEdit = !!document;

  const [isMultiple, setIsMultiple] = useState<boolean>(false);
  const [uploadingCount, setUploadingCount] = useState<number>(0);
  const [title, setTitle] = useState(document?.title ?? "");
  const [files, setFiles] = useState<ApiFile[] | undefined>(document?.file ? [document.file] : []);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const employeeDocumentSave = useEmployeeDocumentSave();
  const orgDocumentSave = useOrganizationDocumentSave();

  const loadFile = useCallback(
    async (file: File) => {
      const formData = new FormData();

      formData.append("file", file);
      formData.append(
        "path",
        employeeId ? ApiFilePathTypes.documents : ApiFilePathTypes.organization_documents
      );
      formData.append("is_public", "false");
      formData.append("save_original_name", "true");
      if (employeeId !== null) formData.append("entity_id", employeeId.toString());

      setIsUploading(true);

      const { data, error } = await apiFiles.add(formData);

      setUploadingCount((prevState) => (prevState > 0 ? prevState - 1 : 0));

      if (data) {
        setFiles((prevState) => {
          return !isEdit ? [...(prevState ?? []), data] : [data];
        });

        if (!title) setTitle(data.name);
      } else {
        handleErrors(error);
      }

      setIsUploading(false);
    },
    [employeeId, handleErrors, isEdit, title]
  );

  const onDrop: FileDropzoneOnDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length + (files ?? []).length > 1 && !isEdit) setIsMultiple(true);
      if (acceptedFiles.length == 0) return;

      const filesForUpload = isEdit ? acceptedFiles.slice(0, 1) : acceptedFiles;

      setUploadingCount(filesForUpload.length);

      filesForUpload.forEach((file: File) => {
        loadFile(file);
      });
    },
    [files, isEdit, loadFile]
  );

  const handleSave = async () => {
    if (!files || files.length == 0) {
      showError({ title: t("errors:no_file_selected") });
      return;
    }

    for (const file of files) {
      try {
        const payload: ApiDocumentSavePayload = {
          title: isEdit ? title : file.name,
          folder_id: document?.folder.id ?? folderId,
          file_uuid: file.uuid,
        };

        const saved = await (employeeId !== null
          ? employeeDocumentSave.mutateAsync({ id: document?.id, employeeId, payload })
          : orgDocumentSave.mutateAsync({ id: document?.id, payload }));

        if (!isEdit) {
          analyticsService.trackEvent(analyticEvents.documents.created, {
            [analyticProperties.id]: saved.id,
          });
        } else {
          analyticsService.trackEvent(analyticEvents.documents.edited, {
            [analyticProperties.id]: document!.id,
          });
        }
      } catch {
        return;
      }
    }

    close(id);
  };

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("core:document")}
      layout={"stretch"}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <Button
            size={"lg"}
            className={"w-full"}
            isLoading={employeeDocumentSave.isPending || orgDocumentSave.isPending}
            onClick={() => {
              handleSave();
            }}
          >
            {t("common:save")}
          </Button>
          <Button
            variant={"plain"}
            size={"lg"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        {!isEdit || isMultiple ? (
          <></>
        ) : (
          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTitle(e.target.value);
            }}
          />
        )}
        <Title header={t("common:file")} size={"xs"} paddingBottom paddingTop={!isMultiple} />
        <FileDropzone
          ctaActionText={t(isEdit ? "common:file" : "common:files").toLowerCase()}
          description={t("common:all_file_types_supported")}
          activeDescription={t("common:upload_file_drag_and_drop")}
          descriptionSize="sm"
          onDrop={onDrop}
          isLoading={isUploading}
          required
        />
        <Stack gap={"sm"} className="mt-4">
          {files?.map((file) => (
            <FileItem
              file={file}
              key={"document-file-" + file.id}
              onOpenFile={async (fileForOpen) => {
                openModal(EmployeeDocumentPreviewModal, {
                  file: fileForOpen,
                  documentTitle: title || fileForOpen.name,
                  analyticsSource: "Document Edit Modal",
                });
              }}
            />
          ))}
          {uploadingCount > 0 && !isEdit ? (
            Array.from({ length: uploadingCount }).map((_, index) => (
              <Skeleton className={"h-7 w-full"} key={`sk-doc-${index}`} />
            ))
          ) : (
            <></>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
